import { memo, Fragment } from "react";
import PropTypes from "prop-types";
import { equals, pick } from "ramda";
import { Flex, SearchInput, Checkbox, IconButton } from "@base-ui";
import { Options, ParentItem, ChildItem, Group } from "./groupedMultiselect.styles";
import utils from "./utils";
import { addRemoveListContent } from "utils/addRemoveListContent";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var GroupedMultiSelect = /*#__PURE__*/memo(function (props) {
  var id = props.id,
    label = props.label,
    options = props.options,
    flatOptions = props.flatOptions,
    searchString = props.searchString,
    search = props.search,
    _onChange = props.onChange,
    parentRef = props.parentRef,
    hideLabel = props.hideLabel,
    placeholder = props.placeholder,
    style = props.style,
    className = props.className,
    disabled = props.disabled,
    disabledLabel = props.disabledLabel;
  var _utils = utils(id, _onChange, search, options, flatOptions, searchString, parentRef),
    open = _utils.open,
    setOpen = _utils.setOpen,
    openGroups = _utils.openGroups,
    setOpenGroups = _utils.setOpenGroups,
    activeFocus = _utils.activeFocus,
    containerRef = _utils.containerRef,
    optionsRef = _utils.optionsRef,
    searchRef = _utils.searchRef,
    handleEvent = _utils.handleEvent,
    handleSearchString = _utils.handleSearchString,
    visibleOptions = _utils.visibleOptions;
  var checkbox = function checkbox(option, idx, className) {
    return /*#__PURE__*/_jsx(Checkbox, {
      id: "".concat(option.id, "-checkbox-").concat(idx),
      label: option.label,
      checked: option.checked,
      disabled: option.disabled,
      onChange: function onChange(add) {
        return _onChange(add, option.id);
      },
      ellipse: true,
      align: "right",
      tabIndex: -1,
      className: className
    });
  };
  var optionsMarkup = function optionsMarkup(option) {
    var _option$children, _option$parents2;
    var idx = visibleOptions.get(option.id);
    if (((_option$children = option.children) === null || _option$children === void 0 ? void 0 : _option$children.length) > 0) {
      var _option$parents;
      //is parent
      var childNodes = option.children.map(function (option) {
        return optionsMarkup(option);
      });
      var isOpen = openGroups.includes(option.id);
      var group = isOpen && /*#__PURE__*/_jsx(Group, {
        role: "group",
        open: isOpen,
        children: childNodes
      }, "".concat(id, "-group-for-").concat(idx));
      return /*#__PURE__*/_jsx(Fragment, {
        children: /*#__PURE__*/_jsxs(ParentItem, {
          id: "".concat(id, "-option-").concat(idx),
          role: "treeitem",
          "aria-checked": option.checked,
          "aria-disabled": option.disabled,
          focused: activeFocus === idx,
          parents: (_option$parents = option.parents) === null || _option$parents === void 0 ? void 0 : _option$parents.length,
          "aria-expanded": openGroups.includes(option.id),
          children: [/*#__PURE__*/_jsxs(Flex.Row, {
            children: [/*#__PURE__*/_jsx(IconButton, {
              label: "Tryck Enter f\xF6r att \xF6ppna/st\xE4nga expandern",
              icon: isOpen ? "expandUp" : "expandDown",
              inverted: true,
              onClick: function onClick() {
                return addRemoveListContent(setOpenGroups, null, !isOpen, option.id);
              }
            }), checkbox(option, idx, "parent"), /*#__PURE__*/_jsx("p", {
              className: "hidden-styles",
              children: "Tryck p\xE5 Space f\xF6r att v\xE4lja/klicka ur checkboxen"
            })]
          }), group]
        })
      }, option.id);
    }
    //is child
    return /*#__PURE__*/_jsx(ChildItem, {
      id: "".concat(id, "-option-").concat(idx),
      role: "treeitem",
      "aria-checked": option.checked,
      "aria-disabled": option.disabled,
      focused: activeFocus === idx,
      parents: (_option$parents2 = option.parents) === null || _option$parents2 === void 0 ? void 0 : _option$parents2.length,
      children: checkbox(option, idx, "child")
    }, option.id);
  };
  var content = options.map(function (option) {
    return optionsMarkup(option);
  });
  var activeDescendant = activeFocus !== -1 ? "".concat(id, "-option-").concat(activeFocus) : undefined;
  return /*#__PURE__*/_jsx(Flex.Row, {
    className: className,
    style: style,
    children: /*#__PURE__*/_jsxs(Flex.Column, {
      ref: containerRef,
      style: {
        position: "relative"
      },
      children: [/*#__PURE__*/_jsx(SearchInput, {
        searchRef: searchRef,
        id: "".concat(id, "-input"),
        ariaHasPopup: "tree",
        ariaExpanded: open,
        ariaControls: "".concat(id, "-tree"),
        ariaAutocomplete: "list",
        value: searchString,
        label: !disabled ? label : disabledLabel,
        search: handleSearchString,
        placeholder: placeholder,
        disabled: disabled,
        title: disabled ? disabledLabel : undefined,
        onFocus: function onFocus() {
          return options.length > 0 && !open && setOpen(true);
        },
        hideLabel: hideLabel,
        icon: "search"
      }), /*#__PURE__*/_jsx(Options, {
        id: "".concat(id, "-tree"),
        role: "tree",
        "aria-activedescendant": activeDescendant,
        "aria-labelledby": "".concat(id, "-input-label"),
        open: open,
        onKeyDown: function onKeyDown(e) {
          return [13, 32].includes(e.keyCode) && handleEvent(e);
        },
        tabIndex: -1,
        ref: optionsRef,
        children: content
      })]
    })
  });
}, function (prev, curr) {
  return equals(pick(["options", "flatOptions", "searchString"], prev), pick(["options", "flatOptions", "searchString"], curr));
});
GroupedMultiSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  flatOptions: PropTypes.array.isRequired,
  searchString: PropTypes.string.isRequired,
  search: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  disabledLabel: PropTypes.string
};
export default GroupedMultiSelect;